import { createApi } from '@reduxjs/toolkit/query/react';
import { AuthType, UsersType, type ApiResponse } from '@types';
import { actions, keepUnusedDataFor } from '../constants/appActions';
import { actionExecution } from '../utils';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { recordAccessExpiry } from '../utils/tokenSession';

export type args = {
  postAuthLogin: {
    username: UsersType.Username;
    password: UsersType.Password;
  };
};
export type responses = {
  postAuthLogin: AuthType.Auth;
};

export const authsApi = createApi({
  reducerPath: 'authsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Auth'],
  endpoints: (builder) => ({
    postAuthLogin: builder.mutation<
    {
      user_id: number;
      name: string;
      access_expires_in?: number;
    },
    args['postAuthLogin']
  >({
    query: (body) => ({
      url: '/user/login/',
      method: 'POST',
      body,
      validateStatus: (response, result) =>
        response.status === 200 && !result.isError,
    }),
    async onQueryStarted(_arg, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        if (data?.access_expires_in) {
          recordAccessExpiry(data.access_expires_in);
        }
      } catch {
        /* login failed */
      }
    },
  }),

    getMe: builder.query<UsersType.UserPartial, void>({
      query: () => ({
        url: '/user/me/',
        method: 'GET',
        credentials: 'include',
        keepUnusedDataFor,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;

        if (data?.id) {
          // Get the rest of the user info including roles and permissions
          const permissions: string[] = [
            ...new Set(
              (data?.roles ?? []).flatMap((role) => role.permissions ?? []),
            ),
          ];

          // Set the user info in the persist store
          actionExecution({
            action: actions.login,
            dispatch,
            args: {
              userid: data.id,
              user: {
                id: data.id,
                username: data.username,
                display_name: data.display_name,
                created_at: data?.created_at,
                email: data?.email,
                roles: data?.roles,
                permissions,
              },
            },
          });
        }
      },
    }),

    postAuthLogout: builder.mutation<ApiResponse<void>, number>({
      query: () => ({
        url: '/user/token/logout/',
        method: 'POST',
        validateStatus: (response) =>
          response.status === 205 || response.status !== 400,
      }),
    }),

    postAuthRefresh: builder.mutation<{ detail?: string; access_expires_in?: number }, void>({
      query: () => ({
        url: '/user/token/refresh/',
        method: 'POST',
        credentials: 'include',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.access_expires_in) {
            recordAccessExpiry(data.access_expires_in);
          }
        } catch {
          /* refresh failed */
        }
      },
    }),

    getMFASetup: builder.query<ApiResponse<AuthType.MFASetup>, void>({
      query: () => ({
        url: `/2fa/setup/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    postMFASetup: builder.mutation<ApiResponse<string>, string>({
      query: (code) => ({
        url: `/2fa/setup/`,
        method: 'POST',
        body: { otp: code },
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    postMFAVerify: builder.mutation<ApiResponse<AuthType.Auth>, string>({
      query: (code) => ({
        url: `/2fa/verify/`,
        method: 'POST',
        body: { otp: code },
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      // use actionExecution in /api/index.tsx for all calls
    }),
  }),
});

export const {
  usePostAuthLoginMutation,
  usePostAuthLogoutMutation,
  usePostAuthRefreshMutation,
  useGetMFASetupQuery,
  usePostMFASetupMutation,
  usePostMFAVerifyMutation,
  useGetMeQuery,
} = authsApi;

export namespace AuthsApi {
  export const Api = authsApi;
  export type Args = args;
  export type Responses = responses;
}
